import React from 'react'
import { pxToRem } from 'theme'
import {
  useInsuranceCompanyForm,
  InsuranceCompanyFieldName,
  getErrorMessage,
  InsuranceCompanyFormValues,
  useEditInsuranceCompany,
  useAddInsuranceCompany,
  InsuranceCompanies_getInsuranceCompanies_insuranceCompanies,
} from '@modmd/data'
import { Dialog } from 'components/Dialog'
import Card from 'components/Card'
import TextInput from 'components/TextInput'
import Button from 'components/Button'
import { FormikHelpers } from 'formik'
import { useToaster } from 'utils/useToaster'
import { Switch } from 'components/Switch'
import { Box } from 'components/Layout'
import { Text } from 'components/Typography'

interface Props {
  isOpen?: boolean
  onDismiss: () => void
  refetch: () => void
  editInsuranceCompanyState?: InsuranceCompanies_getInsuranceCompanies_insuranceCompanies
}

export const CreateCompanyDialog: React.VFC<Props> = ({
  isOpen,
  onDismiss,
  refetch,
  editInsuranceCompanyState,
}) => {
  const { setToastMessage } = useToaster()
  const [addInsuranceCompany] = useAddInsuranceCompany({
    onCompleted: () => {
      setToastMessage('Insurance Company successfully created', 'success')
      refetch()
      onDismiss()
    },
  })
  const [editInsuranceCompany] = useEditInsuranceCompany({
    onCompleted: () => {
      setToastMessage('Insurance Company successfully edited', 'success')
      refetch()
      onDismiss()
    },
  })

  const insuranceCompanyForm = useInsuranceCompanyForm({
    validationSchema: 'insuranceCompany',
    onSubmit: async (
      values: InsuranceCompanyFormValues,
      formikHelpers: FormikHelpers<InsuranceCompanyFormValues>
    ) => {
      if (!editInsuranceCompanyState?.id) {
        await addInsuranceCompany({
          variables: {
            inputData: {
              daysBetweenTests: Number(values.daysBetweenTests) ?? 7,
              name: values.name ?? '',
              pVerifyId: values.pVerifyId ?? '',
              isApproved: values.isApproved ?? true,
            },
          },
        })
      } else {
        await editInsuranceCompany({
          variables: {
            inputData: {
              id: editInsuranceCompanyState.id,
              daysBetweenTests: Number(values.daysBetweenTests) ?? 7,
              name: values.name,
              pVerifyId: values.pVerifyId,
              isApproved: values.isApproved,
            },
          },
        })
      }

      formikHelpers.resetForm()
    },
  })

  React.useEffect(() => {
    if (editInsuranceCompanyState && editInsuranceCompanyState.id) {
      void insuranceCompanyForm.setValues({
        name: editInsuranceCompanyState.name,
        daysBetweenTests: editInsuranceCompanyState.daysBetweenTests,
        pVerifyId: editInsuranceCompanyState.pVerifyId,
        isApproved: editInsuranceCompanyState.isApproved,
      } as InsuranceCompanyFormValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editInsuranceCompanyState])

  const handleDismiss = () => {
    insuranceCompanyForm.resetForm()
    onDismiss()
  }

  return (
    <>
      <Dialog isOpen={isOpen} onDismiss={handleDismiss} maxWidth={pxToRem(600)}>
        <Card>
          <Card.Title onDismiss={handleDismiss}>
            {editInsuranceCompanyState?.id ? 'Edit' : 'Create'} Insurance Company
          </Card.Title>
          <Card.Content display="grid" gridGap="1rem">
            <TextInput
              name={InsuranceCompanyFieldName.NAME}
              value={insuranceCompanyForm.values.name || ''}
              label="Insurance Company Name"
              onChange={insuranceCompanyForm.handleChange}
              errorMessage={getErrorMessage(
                insuranceCompanyForm.touched.name,
                insuranceCompanyForm.errors.name
              )}
              isFullWidth
            />
            <TextInput
              name={InsuranceCompanyFieldName.DAYS_BEETWEEN_TESTS}
              value={insuranceCompanyForm.values.daysBetweenTests}
              label="Days Between Tests"
              onChange={insuranceCompanyForm.handleChange}
              errorMessage={getErrorMessage(
                insuranceCompanyForm.touched.daysBetweenTests,
                insuranceCompanyForm.errors.daysBetweenTests
              )}
              isFullWidth
            />
            <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between">
              <Box flex="2">
                <TextInput
                  name={InsuranceCompanyFieldName.PVERIFY_ID}
                  value={insuranceCompanyForm.values.pVerifyId || ''}
                  label="Insurance Company PVerify ID"
                  onChange={insuranceCompanyForm.handleChange}
                  errorMessage={getErrorMessage(
                    insuranceCompanyForm.touched.pVerifyId,
                    insuranceCompanyForm.errors.pVerifyId
                  )}
                  isFullWidth
                />
              </Box>
              <Box
                flex="1"
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-around"
              >
                <Switch
                  size="small"
                  isChecked={!!insuranceCompanyForm.values.isApproved}
                  onChange={async (e) => {
                    await insuranceCompanyForm.setFieldValue('isApproved', e)
                  }}
                />
                <Text mr="1.25rem" fontSize="m">
                  Approved
                </Text>
              </Box>
            </Box>

            <Card.Actions>
              <Button colorVariant="secondary" mr="0.25rem" onClick={handleDismiss}>
                Cancel
              </Button>
              <Button
                disabled={!insuranceCompanyForm.isValid}
                onClick={() => {
                  insuranceCompanyForm.handleSubmit()
                }}
                isFetching={insuranceCompanyForm.isSubmitting}
              >
                Save
              </Button>
            </Card.Actions>
          </Card.Content>
        </Card>
      </Dialog>
    </>
  )
}
